import { EventEmitter } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormControl,
  AbstractControl
} from "@angular/forms";

import * as _ from "lodash";

import { FormSchema } from "./schema";
import { SetupSchemaMask } from "../form.mask";
import {
  FormSchemaData,
  FormSchemaGroupData,
  FormSchemaGroup,
  FormValues,
  FormSubmitHandler,
  FormSubmitEvent,
  AddSchemaOptions
} from "../form.interface";

import { ToastService } from "../../toast/toast.service";

export class FormData {
  constructor(
    private _index: number,
    private _formBuilder: FormBuilder,
    private _schemaData: Array<FormSchemaData>,
    private _groupsData: Array<FormSchemaGroupData>,
    private _toastService: ToastService,
    public submitHandler: FormSubmitHandler
  ) {
    this._createFormGroup();
    this._setGroups();
    for (const schema_item of this._schemaData) {
      this.addSchema(schema_item, null);
    }
  }

  public group: FormGroup;

  //// Public values

  public schema: Array<FormSchema> = [];
  public groups: Array<FormSchemaGroup> = [];

  public locked = false;

  public onValuesSetted: EventEmitter<FormValues> = new EventEmitter();
  public onErrorsSetted: EventEmitter<any> = new EventEmitter();
  public onSubmit: EventEmitter<FormSubmitEvent> = new EventEmitter();

  // For internal use
  public $onSendSubmit: EventEmitter<any> = new EventEmitter();

  private _schema_indexes: Object = {};
  private _schema_index = 1;

  public lock() {
    this.locked = true;
  }

  public unlock() {
    this.locked = false;
  }

  public addSchema(
    schema: FormSchemaData,
    options?: AddSchemaOptions
  ): FormSchema {
    if (!schema.key) {
      schema.key = `input-${this._index}${this._schema_index}`;
    }
    this._schema_index++;

    // Aplica a mascara inicial
    SetupSchemaMask(schema);

    // Adiciona o schema ao FormGroup
    const control = new FormControl(schema.value);
    this.group.addControl(schema.key, control);

    // Cria uma instancia de FormSchema<T>
    const schema_instance = new FormSchema(schema, control, this);

    if (this.group.dirty && this.group.touched) {
      schema_instance.validate();
    }

    // Adiciona o schema ao array de schemas
    this.schema.push(schema_instance);

    this._updateSchemaIndexes();
    this._addSchemaToGroup(schema_instance, options);
    this.group.updateValueAndValidity({ onlySelf: true });

    return schema_instance;
  }

  public removeSchema(key: string) {
    this.group.removeControl(key);

    const schema_index = this.schema.findIndex(
      (schema: FormSchema) => schema.key === key
    );
    if (schema_index >= 0) {
      this._removeSchemaFromGroup(this.schema.splice(schema_index, 1)[0]);
      this.group.updateValueAndValidity({ onlySelf: true });
    }

    this._updateSchemaIndexes();
  }

  // Busca um item de schema pela 'key' ou todos os schemas em forma de objeto
  public getSchema(key?: string): FormSchema {
    const index = this._schema_indexes[key];
    if (key && _.isNumber(index)) {
      return this.schema[index];
    }
    return null;
  }

  public getSchemas(): { [key: string]: FormSchema } {
    const schemas: { [key: string]: FormSchema } = {};
    this.schema.forEach(
      (schema_item: FormSchema) => (schemas[schema_item.key] = schema_item)
    );
    return schemas;
  }

  public getControl(key?: string): any {
    if (!key) return this.schema.map(schema => schema.getControl());

    const schema = this.getSchema(key);
    if (!schema) return null;

    return schema.getControl();
  }

  public setValues(values: FormValues) {
    if (_.isEmpty(values)) {
      return;
    }
    this.schema.forEach((schema: FormSchema) => {
      if (
        _.isEmpty(schema.key) ||
        Object.keys(values).indexOf(schema.key) == -1
      ) {
        return;
      }
      schema.value = values[schema.key];
    });
    this.onValuesSetted.emit(values);
  }
  public getValues(include_disabled: boolean = false): FormValues {
    const values: FormValues = {};
    this.schema.forEach((schema: FormSchema) => {
      const allow =
        include_disabled || (!include_disabled && !schema.isControlDisabled());
      if (schema.key && allow) {
        values[schema.key] = schema.value;
      }
    });
    return values;
  }
  public set value(values: FormValues) {
    this.setValues(values);
  }
  public get value(): FormValues {
    return this.getValues();
  }

  public setDefaultValues(values: FormValues) {
    if (_.isEmpty(values)) {
      return;
    }
    this.schema.forEach((schema: FormSchema) => {
      if (
        _.isEmpty(schema.key) ||
        Object.keys(values).indexOf(schema.key) == -1
      ) {
        return;
      }
      schema.default_value = values[schema.key];
    });
  }
  public set default_value(values: FormValues) {
    this.setDefaultValues(values);
  }
  public get default_value(): FormValues {
    const values: FormValues = {};
    this.schema.forEach((schema: FormSchema) => {
      if (schema.key) {
        values[schema.key] = schema.default_value;
      }
    });
    return values;
  }

  public setErrors(errors: any) {
    if (_.isPlainObject(errors)) {
      this.schema.forEach((schema_item: FormSchema) => {
        if (schema_item.key && errors[schema_item.key]) {
          let error: any = errors[schema_item.key];
          if (_.isArray(error)) {
            error = error[0];
            schema_item.setCustomError(error);
          } else {
            schema_item.setErrors(error);
          }
        }
      });
      this.onErrorsSetted.emit(errors);
    }
  }
  public get errors(): any {
    const controls = this.group.controls;
    const errors: any = {};
    _.forEach(controls, (control_value, control_key) => {
      const control: AbstractControl = controls[control_key];
      if (control.errors) {
        errors[control_key] = control.errors;
      }
    });
    return errors;
  }

  public get valid(): boolean {
    return this.group.valid;
  }

  public submit() {
    this.$onSendSubmit.emit();
  }

  public reset() {
    this.schema.forEach(schema => schema.reset());
  }

  public validate(focus_first: boolean = false): boolean {
    this.schema.forEach(_schema => _schema.validate());

    this.group.updateValueAndValidity({ onlySelf: true });

    if (this.group.valid) return true;

    if (focus_first) {
      let found_error = false;

      this.schema.forEach(schema => {
        if (!found_error && !_.isEmpty(schema.errors)) {
          found_error = true;
          schema.focus();
        }
      });
    }

    this._onInvalidForm();

    return false;
  }

  public hasLabels(): boolean {
    return this.schema
      ? this.schema.filter(item => !!item.label).length > 0
      : true;
  }

  public handleError(err: any) {
    if (_.isPlainObject(err) && err.data && err.status != 0) {
      const error = err.data;
      if (error.errors) {
        this.setErrors(error.errors);
      }
      this._toastService.open({
        message: error.message
      });
    } else {
      console.error(err);
      this._toastService.open({
        message: "Ocorreu um erro desconhecido."
      });
    }
  }

  public updateControlDisabled() {
    this.schema.forEach(schema => {
      if (schema.disabled) {
        schema.updateControlDisabled();
      }
    });
  }

  //// Private methods

  // Manipula a aÃ§Ã£o quando o formulÃ¡rio Ã© invÃ¡lido
  private _onInvalidForm() {
    this._toastService.open({
      message: "Ops, ainda existem campos invÃ¡lidos...",
      duration: 3000
    });
  }

  // Analisa e gera os validadores, seta os valores padrÃ£o e gera o FromGroup
  private _createFormGroup() {
    this.group = this._formBuilder.group({});
  }

  private _updateSchemaIndexes() {
    this._schema_indexes = {};
    this.schema.forEach((item, index) => {
      this._schema_indexes[item.key] = index;
    });
  }

  // Cria os grupos de schema
  private _setGroups() {
    if (this.groups.length) return;

    const groups: { [key: string]: FormSchemaGroup } = {
      $ungrouped: {
        key: "$ungrouped",
        label: null,
        icon: null,
        iconMode: null,
        schema: []
      }
    };

    this._groupsData.forEach(group => {
      groups[group.key] = {
        key: group.key,
        label: group.label,
        icon: group.icon,
        description: group.description,
        iconMode: group.iconMode,
        schema: []
      };
    });

    Object.keys(groups).forEach(key => {
      const group = groups[key];

      if (group.icon && !group.iconMode) {
        group.iconMode = "md";
      }

      this.groups.push(group);
    });
  }

  public _addSchemaToGroup(schema: FormSchema, options?: AddSchemaOptions) {
    if (!schema) return;

    const group_index = this.groups.findIndex(group => {
      if (schema.group && group.key !== schema.group) return false;
      if (!schema.group && group.key !== "$ungrouped") return false;

      return true;
    });

    const group = group_index !== -1 && this.groups[group_index];
    if (!group) return;

    if (!options || (!options.before && !options.after)) {
      return group.schema.push(schema);
    }

    const is_before = options.before ? true : false;
    const reference_key = options.before ? options.before : options.after;
    const reference_index = group.schema.findIndex(
      item => item.key == reference_key
    );

    if (reference_index === -1) {
      return group.schema.push(schema);
    }

    group.schema.splice(
      is_before ? reference_index : reference_index + 1,
      0,
      schema
    );
  }

  public _removeSchemaFromGroup(schema: FormSchema) {
    if (!schema) return;

    const group_index = this.groups.findIndex(group => {
      if (schema.group && group.key !== schema.group) return false;
      if (!schema.group && group.key !== "$ungrouped") return false;

      return true;
    });

    const group = group_index !== -1 && this.groups[group_index];
    if (!group) return;

    const schema_index = group.schema.findIndex(
      _schema => _schema.key === schema.key
    );
    group.schema.splice(schema_index, 1);
  }
}
